
import { logger } from "@/utils/logger";
import {Options, Vue} from "vue-class-component";
import store from "@/store";
import {PartyRelation, RelationType, Third, ThirdType} from "@/types";
import {add, addExpandedItem, edit, removeExpandedItem} from "@/utils";
import ThirdSearchDialog from "@/components/demand/dialogs/ThirdSearchDialog.vue";
import Notify from 'quasar/src/plugins/Notify.js';;
import {formatDateLocale} from "@/utils/configuration/formatters-config";
import i18n from "@/i18n";

@Options({
  props: ["party"],
  components: {
    ThirdSearchDialog,
  },
  computed: {

    isPersonMorale: function () {
      return this.party?.type.id.toUpperCase().includes("ORGANIZATION");
    },
    isPersonPhysique: function () {
      return this.party?.type.id.toUpperCase().includes("PERSON");
    },
  },
  watch: {
    party: {
      deep: true,
      immediate: true,
      handler(this: TaskPartyRelation) {
        const rel =
            this.party?.type?.id === ThirdType.PERSON
                ? this.party?.person?.relations
                : this.party?.organization?.relations;

        this.relations = rel;
        if (this.relations && this.relations.length === 0) {
          this.add(this.relations);
        } else if (this.relations?.length) {
          this.syncRelationDirections(this.relations);
        }
      },
    },
  },
})
export default class TaskPartyRelation extends Vue {
  relationSelectedIndex?: number;
  relationSelectedType?: ThirdType.ORGANIZATION | ThirdType.PERSON;
  openedRelations:boolean = false;
  dateMask = "DD/MM/YYYY";
  showDialog?: boolean = false;
  openDialog = "";
  party?:Third;
  relationBtn = false;
  reset: any;
  relations:any;

  get organizationType() {
    return ThirdType.ORGANIZATION;
  }

  get personType() {
    return ThirdType.PERSON;
  }

  /* Picklists */

  get relationsPicklist() {
    return store.getters["picklistModule/relationsItems"].map((item: any) => {
      return {
        ...item,
        label: item.value?.toString() !== "" ? i18n.global.t("party.relation.relation." + item.value) : ''
      };
    });
  }
  get FR() {
  return {
    days: ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'],
    daysShort: ['dim', 'lun', 'mar', 'mer', 'jeu', 'ven', 'sam'],
    months: ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'],
    monthsShort: ['janv', 'févr', 'mars', 'avr', 'mai', 'juin', 'juil', 'août', 'sept', 'oct', 'nov', 'déc']
   }
  }


  onRelationTypeChange(value: any) {
    if(value){
      store.dispatch("taskModule/handleRelations", value);

    }
  }

  /** Who this party is in the link decides how we read it — not the stored combo alone.
   *  partyDesc → ascending; partyAsc → descending (same rule as buildRelations). */
  getCurrentPartyUid(): string {
    return (
      this.party?.person?.resourceUid ||
      this.party?.organization?.resourceUid ||
      this.party?.id?.resourceUid ||
      (this.party as any)?.resourceUid ||
      ""
    );
  }

  getEffectiveRelationDirection(filterValue: any): string {
    const currentUid = this.getCurrentPartyUid();
    const partyDescUid = filterValue?.partyDesc?.resourceUid;
    const partyAscUid = filterValue?.partyAsc?.resourceUid;

    if (currentUid && partyDescUid && String(currentUid) === String(partyDescUid)) {
      return RelationType.ASCENDING;
    }
    if (currentUid && partyAscUid && String(currentUid) === String(partyAscUid)) {
      return RelationType.DESCENDING;
    }
    return filterValue?.relation?.type?.value || "";
  }

  /** Keep the Asc/Desc combo aligned with the perspective of the party being edited. */
  syncRelationDirections(relations: any[]) {
    if (!Array.isArray(relations)) {
      return;
    }
    relations.forEach((rel: any) => {
      const direction = this.getEffectiveRelationDirection(rel);
      if (!direction) {
        return;
      }
      if (!rel.relation) {
        rel.relation = { type: { value: direction, label: "" } };
      } else if (!rel.relation.type) {
        rel.relation.type = { value: direction, label: "" };
      } else if (rel.relation.type.value !== direction) {
        rel.relation.type.value = direction;
      }
    });
  }

  translateDirectionalLabel(typeCode: string, direction: string): string | undefined {
    const key = `party.relation.relationTypeLabel.${typeCode}_${direction.toUpperCase()}`;
    const translated = i18n.global.t(key);
    return translated.includes("party.relation.relationTypeLabel.")
      ? undefined
      : translated;
  }

  getRelationTypeDisplayLabel(typeCode: string, direction: string): string {
    if (!typeCode) {
      return "";
    }
    const directional = this.translateDirectionalLabel(typeCode, direction);
    if (directional) {
      return directional;
    }
    const translated = i18n.global.t("party.relation.relationType." + typeCode);
    return translated.includes("party.relation.relationType.") ? typeCode : translated;
  }

  /** Same rule as TabRelation: the inverse reading is a separate type (`APTGRP` → `APFIL`). */
  getInverseRelationType(item: any) {
    const allItems: any[] = store.getters["picklistModule/relationTypeItems"] || [];
    const named = allItems.find((other: any) => other.value === item?.inverse_label);
    if (named) {
      return named;
    }
    return allItems.find(
      (other: any) => other.inverse_label === item?.value && other.value !== item?.value
    );
  }

  getRelationType(filterValue: any) {
    const relationDirection = this.getEffectiveRelationDirection(filterValue);
    const currentTypeValue = filterValue.relationType?.type?.value;
    const allItems: any[] = store.getters["picklistModule/relationTypeItems"] || [];

    if (
      relationDirection !== RelationType.ASCENDING &&
      relationDirection !== RelationType.DESCENDING
    ) {
      return null;
    }

    const readsInDirection = (item: any) => {
      const configured =
        relationDirection === RelationType.ASCENDING
          ? item.ascendinglabel
          : item.descendinglabel;
      return String(configured ?? "").toLowerCase() === relationDirection;
    };

    // Old code only inverted when direction was DESCENDING and the item was ascending-only
    // (e.g. APFIL). That missed the opposite case: APTGRP (descending-only) read as ascending.
    // Now: if the stored type does not read in this party's direction, label it with its inverse.
    const resolveItemLabel = (item: any) => {
      const isInverseCase =
        currentTypeValue === item.value &&
        !!item.inverse_label &&
        !readsInDirection(item);

      const typeCode = isInverseCase
        ? (this.getInverseRelationType(item)?.value || item.inverse_label)
        : item.value;
      return this.getRelationTypeDisplayLabel(typeCode, relationDirection);
    };

    let items = allItems.filter(readsInDirection).map((item: any) => ({
      ...item,
      label: this.getRelationTypeDisplayLabel(item.value, relationDirection)
    }));

    if (currentTypeValue && !items.some((item: any) => item.value === currentTypeValue)) {
      const storedItem = allItems.find((item: any) => item.value === currentTypeValue);
      if (storedItem) {
        items = [
          ...items.filter((item: any) => item.label !== resolveItemLabel(storedItem)),
          { ...storedItem, value: currentTypeValue, label: resolveItemLabel(storedItem) }
        ];
      }
    }

    return items;
  }

  get clientType() {
    return store.getters["picklistModule/clientTypeItems"];
  }

  /* Methods */

  addExpandRelation(items: PartyRelation[]) {
    if (items && items.length === 0) {
      const element = store.getters["thirdModule/getRelationInit"];
      this.relations?.push(element);
    }
  }

  add<T>(items: T[]) {
    const getter = store.getters['thirdModule/getRelationInit'];
    items.push(getter);
  }

  edit = edit;

  addExpandedItem = addExpandedItem;
  removeExpandedItem = removeExpandedItem;
  startDate!: string;

  remove <T>(index: string | number, items: T[]) {
    if (items) {
      items.splice(Number(index), 1);
    }
  }
  setDeletedRelation (relation: PartyRelation) {
    store.commit("thirdModule/addDeletedRelationUid", relation.resourceUid)
  }
  formatDateUntil(value: any) {
    value.validity.until = value.validity.until.includes("/")
        ? formatDateLocale(value.validity.until, "DD/MM/YYYY", "fr", "DD/MM/YYYY")
        : formatDateLocale(value.validity.until, "DDMMYYYY", "fr", "DD/MM/YYYY");
  }
  formatDateFrom(value: any) {
    value.validity.from = value.validity.from.includes("/")
        ? formatDateLocale(value.validity.from, "DD/MM/YYYY", "fr", "DD/MM/YYYY")
        : formatDateLocale(value.validity.from, "DDMMYYYY", "fr", "DD/MM/YYYY");
  }

  searchThird(index: number, type: ThirdType.ORGANIZATION | ThirdType.PERSON) {
    this.relationSelectedIndex = index;
    this.showDialog = true;
    this.relationSelectedType = type;
    this.openDialog = type;
  }

  onSelectedItem(third: any) {
    //if (this.validateSelectedItem(third)) {
      const index = this.relationSelectedIndex || 0;
      const relations = this.party?.person?.relations || this.party?.organization?.relations;
      const party = this.party?.person || this.party?.organization;
      if (relations && index >= 0 && index < relations.length && relations[index]) {
        if (third._discriminator === ThirdType.ORGANIZATION) {
          relations[index].name = !third.name ? third.commercial_name : third.name;
        } else {
          relations[index].name = third.familyname;
        }
        if (relations[index].relation?.type?.value === RelationType.ASCENDING) {
          relations[index].partyAsc = { resourceUid: third.id };
          relations[index].partyDesc = { resourceUid: party?.resourceUid };
        } else {
          relations[index].partyDesc = { resourceUid: third.id };
          relations[index].partyAsc = { resourceUid: party?.resourceUid };
        }
        this.onClose();
      }
   /* } else {
      logger.warn(`Relation avec ${third.firstname} existe déjà!`);
      Notify.create({
        timeout: 10000,
        actions: [{ icon: 'close', color: 'white' }],
        message: `Relation avec ${third.firstname} existe déjà!`,
        color: "negative",
      });
    } */
  }

  validateSelectedItem(third: Third): boolean {
    const relations = [
      ...(this.party?.person?.relations || []),
      ...(this.party?.organization?.relations || [])
    ];

    return !relations.some((item) => {
      const relationId =
          item.relation?.type?.value === RelationType.ASCENDING
              ? item.partyAsc?.resourceUid
              : item.partyDesc?.resourceUid;

      return relationId === third.id;
    });
  }


  onClose() {
    this.showDialog = false;
  }

  changeRelation(value: any) {
    const asc = value.partyAsc;
    const desc = value.partyDesc;

    if (value.relation.type.value === RelationType.ASCENDING) {
      value.partyAsc = desc;
      value.partyDesc = asc;
    } else {
      value.partyDesc = asc;
      value.partyAsc = desc;
    }

    const options = this.getRelationType(value);

    if (Array.isArray(options) && options.length) {
      value.relationType.type.value = options[0]?.value ?? null;
    }else {
      value.relationType.type.value = null;
    }
  }

  getStartDate(index: number, value: any) {
    this.startDate = formatDateLocale(value, "DD/MM/YYYY", "us", "YYYY/MM/DD");
  }

  limitEndDateOptions(date: any) {
    return date > this.startDate;
  }
  formatDate(date: string) {
    return formatDateLocale(date, this.dateMask, "fr", "YYYY/MM/DD");
  }
}
